import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableHead, 
  TableHeader, 
  TableRow 
} from '../../components/ui/table';
import { useAuth } from '../../context/auth-context';
import { API_URL } from '../../config/api';
import { toast } from '../../components/ui/use-toast';
import { 
  Users, 
  Activity, 
  AlertCircle, 
  Search, 
  RefreshCw,
  Calendar,
  MessageSquare,
  TrendingUp,
  CreditCard,
  Gift
} from 'lucide-react';

interface UserLicense {
  user_id: string;
  email: string;
  name: string;
  site_id: string;
  site_url: string;
  site_name: string;
  plan_name: string;
  license_status: string;
  billing_period: string;
  conversations_today: number;
  total_quota: number;
  quota_exhausted: boolean;
  referral_bonus: number;
  license_created: string;
  trial_ends_at: string | null;
}

interface LicenseSummary {
  totalUsers: number;
  activeToday: number;
  exhaustedQuota: number;
}

export default function LicenseManagement() {
  const { token } = useAuth();
  const [licenses, setLicenses] = useState<UserLicense[]>([]);
  const [summary, setSummary] = useState<LicenseSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filter, setFilter] = useState<'all' | 'active' | 'exhausted'>('all');

  useEffect(() => {
    if (token) {
      fetchLicenses();
    }
  }, [token, selectedDate]);

  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/admin/licenses?date=${selectedDate}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Failed to fetch licenses');

      const data = await response.json();
      setLicenses(data.users);
      setSummary(data.summary);
    } catch (error) {
      console.error('Error fetching licenses:', error);
      toast({
        title: 'Error',
        description: 'Failed to load license data',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  };

  const getFilteredLicenses = () => {
    let filtered = licenses;

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(license => 
        license.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
        license.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        license.site_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        license.site_url?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Apply status filter
    if (filter === 'active') {
      filtered = filtered.filter(license => license.conversations_today > 0);
    } else if (filter === 'exhausted') {
      filtered = filtered.filter(license => license.quota_exhausted);
    }

    return filtered;
  };

  const getUsageColor = (used: number, total: number) => {
    const percentage = (used / total) * 100;
    if (percentage >= 100) return 'text-red-600';
    if (percentage >= 80) return 'text-orange-600';
    if (percentage >= 50) return 'text-yellow-600';
    return 'text-green-600';
  };

  const getPlanBadge = (plan: string) => {
    const variants: Record<string, any> = {
      'Free': 'secondary',
      'Starter': 'outline',
      'Growth': 'default',
      'Pro': 'default',
      'Enterprise': 'destructive'
    };
    return <Badge variant={variants[plan] || 'secondary'}>{plan}</Badge>;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return <Badge variant="success">Active</Badge>;
      case 'suspended':
        return <Badge variant="warning">Suspended</Badge>;
      case 'cancelled':
        return <Badge variant="secondary">Cancelled</Badge>;
      case 'expired':
        return <Badge variant="destructive">Expired</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredLicenses = getFilteredLicenses();

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">License Management</h1>
        <p className="text-muted-foreground">Monitor user licenses and usage across all sites</p>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Users className="h-4 w-4" />
                Total Licensed Users
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summary.totalUsers}</div>
              <p className="text-xs text-muted-foreground">Across all sites</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Active Today
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-green-600">{summary.activeToday}</div>
              <p className="text-xs text-muted-foreground">Had conversations</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Exhausted Quota
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{summary.exhaustedQuota}</div>
              <p className="text-xs text-muted-foreground">Reached daily limit</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <Card className="mb-6">
        <CardHeader>
          <CardTitle>Filters</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="search">Search</Label>
              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  id="search"
                  placeholder="Search users, sites..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant={filter === 'all' ? 'default' : 'outline'}
                  onClick={() => setFilter('all')}
                >
                  All
                </Button>
                <Button
                  size="sm"
                  variant={filter === 'active' ? 'default' : 'outline'}
                  onClick={() => setFilter('active')}
                >
                  Active
                </Button>
                <Button
                  size="sm"
                  variant={filter === 'exhausted' ? 'default' : 'outline'}
                  onClick={() => setFilter('exhausted')}
                >
                  Exhausted
                </Button>
              </div>
            </div>

            <div className="flex items-end">
              <Button onClick={fetchLicenses} disabled={loading}>
                <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* License Table */}
      <Card>
        <CardHeader>
          <CardTitle>User Licenses</CardTitle>
          <CardDescription>
            Showing {filteredLicenses.length} of {licenses.length} licenses
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Site</TableHead>
                  <TableHead>Plan</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Usage Today</TableHead>
                  <TableHead>Bonus</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">
                      <RefreshCw className="h-6 w-6 animate-spin mx-auto mb-2" />
                      Loading licenses...
                    </TableCell>
                  </TableRow>
                ) : filteredLicenses.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No licenses found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLicenses.map((license) => (
                    <TableRow key={`${license.user_id}-${license.site_id}`}>
                      <TableCell>
                        <div>
                          <div className="font-medium">{license.name || 'Unknown'}</div>
                          <div className="text-sm text-muted-foreground">{license.email}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{license.site_name}</div>
                          <div className="text-sm text-muted-foreground truncate max-w-xs">
                            {license.site_url}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          {getPlanBadge(license.plan_name)}
                          <div className="text-xs text-muted-foreground">
                            {license.billing_period}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {getStatusBadge(license.license_status)}
                      </TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <div className={`font-medium ${getUsageColor(license.conversations_today, license.total_quota)}`}>
                            {license.conversations_today} / {license.total_quota}
                          </div>
                          {license.quota_exhausted && (
                            <Badge variant="destructive" className="text-xs">
                              Limit Reached
                            </Badge>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        {license.referral_bonus > 0 && (
                          <div className="flex items-center gap-1">
                            <Gift className="h-3 w-3 text-purple-600" />
                            <span className="text-sm">+{license.referral_bonus}</span>
                          </div>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost">
                          View Details
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}